import { Components } from '@ionic/core/dist/types/components';
import { IonIcon, IonInput, IonLabel, isPlatform } from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import { lockClosed, lockOpenOutline } from 'ionicons/icons';
import { isNaN } from 'lodash';
import React, {
  HTMLAttributes,
  ReactElement,
  useCallback,
  useRef,
} from 'react';
import { useGetUnitOfMeasureSystem } from '../../api/hooks/user-preference.hooks';
import { WidgetLabel } from '../../scratchpad/application/shared/component/widget/widget-label';
import { SystemUnit } from '../../scratchpad/application/shared/model/flow.types';
import { isEnter } from '../../util/keyboard';
import { BaseButton } from '../button/base-button';
import { IonItem } from '../ionic/IonItem';
import './ion-form-numeric-input-lockable.css';

export type NumericInputPreferredUnits = Record<SystemUnit, string>;

export interface IonFormNumericInputLockableProps extends FieldConfig {
  className?: string;
  label: string;
  isLast?: boolean;
  preferredUnits?: NumericInputPreferredUnits;
  isLocked: boolean;
  onToggleLock: () => void;
  itemProps?: Partial<Components.IonItem> &
    Partial<HTMLAttributes<HTMLIonItemElement>> &
    Partial<React.RefAttributes<HTMLIonItemElement>>;
  inputProps?: Partial<Components.IonInput> &
    Partial<HTMLAttributes<HTMLIonInputElement>> &
    Partial<React.RefAttributes<HTMLIonInputElement>>;
  lockProps?: Partial<Pick<HTMLIonIconElement, 'id'>>;
}

const IonFormNumericInputLockable = (
  props: IonFormNumericInputLockableProps
): ReactElement => {
  const {
    label,
    itemProps,
    inputProps,
    isLast,
    preferredUnits,
    onToggleLock,
    isLocked,
    lockProps,
    ...otherProps
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);

  const system = useGetUnitOfMeasureSystem();

  const inputRef = useRef<HTMLIonInputElement>(null);

  const showLockIcon = isLocked ? true : false;

  const { className: itemClassName, ...restItemProps } = itemProps ?? {};
  let itemClasses = ['item-numeric-input-lockable transparent-background'];

  if (itemClassName) {
    itemClasses = itemClasses.concat(itemClassName.split(' '));
  }

  const selectInput = useCallback(async () => {
    if (isPlatform('capacitor')) {
      const el = await inputRef.current?.getInputElement();
      el?.select();
    }
  }, []);

  const blurIfEnter = async (e: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (isEnter(e)) {
      const nativeInput = await inputRef.current?.getInputElement();
      nativeInput?.blur();
    }
  };

  return (
    <IonItem lines="full" className={itemClasses.join(' ')} {...restItemProps}>
      <IonInput
        ref={inputRef}
        labelPlacement={'start'}
        type="number"
        onIonInput={(e) => {
          const value = e.detail.value;
          const convertedValue = Number(value);
          const numValue = isNaN(convertedValue) ? 0 : convertedValue;
          helper.setValue(numValue);
        }}
        enterkeyhint={'done'}
        onKeyDown={blurIfEnter}
        onIonFocus={selectInput}
        {...inputProps}
        {...field}
      >
        <div slot="label">
          <WidgetLabel
            label={label}
            // We are setting this class to override the 'ion-text-wrap' class
            ionLabelProps={{ className: 'widget-label' }}
            error={meta.error}
          />
        </div>
      </IonInput>
      {preferredUnits && (
        <IonLabel slot="end">
          &nbsp;{getConversionUom(system, preferredUnits)}
        </IonLabel>
      )}
      <BaseButton
        buttonProps={{
          slot: 'end',
          fill: 'clear',
          onClick: () => {
            onToggleLock();
          },
        }}
      >
        <IonIcon
          id={lockProps?.id}
          slot="icon-only"
          icon={showLockIcon ? lockClosed : lockOpenOutline}
          color={showLockIcon ? 'primary' : 'medium'}
        />
      </BaseButton>
    </IonItem>
  );
};

const getConversionUom = (
  system: SystemUnit,
  preferredUnits: NumericInputPreferredUnits
): string => {
  return preferredUnits[system];
};

export { IonFormNumericInputLockable };
